import { Fragment, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ACTIVE_THREAD_STATES, type AgentStatus } from '@hub/contracts'
import type { ChatMessage, QueuedMessage } from '@/lib/collab/use-chat-collab'
import { MessageQueue } from '@/components/assistant-ui/elements/message-queue'
import { mono } from '@/components/assistant-ui/elements/surfaces'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { AssistantMessage } from './assistant-message'
import { UserMessage } from './user-message'
import { formatDay } from './time'

/** Day divider — the assistant-ui day-separator element's rule + label. */
function DayDivider({ label }: { label: string }) {
  return (
    <div data-slot="day-separator" className="flex items-center gap-2.5 py-1">
      <span className="bg-foreground/[0.08] h-px flex-1" />
      <span className={cn(mono, 'text-foreground/30')}>{label}</span>
      <span className="bg-foreground/[0.08] h-px flex-1" />
    </div>
  )
}

interface ChatThreadProps {
  messages: ChatMessage[]
  projectId: string
  currentUserId?: string
  agentStatus: AgentStatus
  messageQueue: QueuedMessage[]
  typingUsers: { userId: string; name: string }[]
  hasOlderMessages: boolean
  loadingOlder: boolean
  onLoadOlder: () => void
  onRetry: (messageId: string) => void
  /** Board 5q82: also offer Retry on a failed opening message (no user message before it). */
  retryFirstTurn?: boolean
  retryingMessageId: string | null
  onDeleteOwnMessage: (messageId: string) => void
  onRemoveQueued: (id: string) => void
}

export function ChatThread({
  messages,
  projectId,
  currentUserId,
  agentStatus,
  messageQueue,
  typingUsers,
  hasOlderMessages,
  loadingOlder,
  onLoadOlder,
  onRetry,
  retryFirstTurn,
  retryingMessageId,
  onDeleteOwnMessage,
  onRemoveQueued,
}: ChatThreadProps) {
  const { t } = useTranslation('chat')
  // The assistant message currently being produced (by id from the status
  // map, falling back to the trailing assistant message while active).
  const liveMessageId = useMemo(() => {
    if (agentStatus.state === 'idle') return null
    if (agentStatus.messageId) return agentStatus.messageId
    const last = messages[messages.length - 1]
    return last?.role === 'assistant' ? last.id : null
  }, [agentStatus.state, agentStatus.messageId, messages])

  // Retry is offered on failed assistant messages that have a preceding user
  // message to re-send — or, with `retryFirstTurn` (the onboarding thread,
  // where HUB speaks first), on a failed opening message too.
  const retryable = useMemo(() => {
    const out = new Set<string>()
    let sawUser = false
    for (const m of messages) {
      if (m.role === 'user') sawUser = true
      else if (m.role === 'assistant' && m.error && (sawUser || retryFirstTurn)) out.add(m.id)
    }
    return out
  }, [messages, retryFirstTurn])

  // Only the latest HUB reply animates its avatar; the rest are stills.
  const latestAssistantId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant') return messages[i].id
    }
    return null
  }, [messages])
  const busy = ACTIVE_THREAD_STATES.has(agentStatus.state)

  const runningText = useMemo(() => {
    if (agentStatus.state === 'idle' || agentStatus.state === 'error') return null
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'user') return messages[i].content
    }
    return null
  }, [agentStatus.state, messages])

  let lastDay = ''

  return (
    <div data-slot="chat-thread" className="flex flex-col gap-4">
      {hasOlderMessages && (
        <div className="flex justify-center py-2">
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground text-xs"
            onClick={onLoadOlder}
            disabled={loadingOlder}
          >
            {loadingOlder ? t('thread.loading') : t('thread.loadOlder')}
          </Button>
        </div>
      )}

      {messages.map((message) => {
        const day = formatDay(message.createdAt)
        const newDay = day !== lastDay
        lastDay = day
        const isLive = message.id === liveMessageId
        return (
          <Fragment key={message.id}>
            {newDay && day && <DayDivider label={day} />}
            {message.role === 'assistant' ? (
              <AssistantMessage
                message={message}
                projectId={projectId}
                liveStatus={isLive ? agentStatus : undefined}
                latest={message.id === latestAssistantId}
                working={busy}
                onRetry={retryable.has(message.id) ? onRetry : undefined}
                retrying={retryingMessageId === message.id}
              />
            ) : (
              <UserMessage
                message={message}
                projectId={projectId}
                onDelete={
                  currentUserId && message.userId === currentUserId ? onDeleteOwnMessage : undefined
                }
              />
            )}
          </Fragment>
        )
      })}

      {messageQueue.length > 0 && runningText && (
        <MessageQueue
          running={runningText}
          queued={messageQueue.map((q) => ({ id: q.id, text: q.content }))}
          onCancel={onRemoveQueued}
          className="max-w-none"
        />
      )}

      {typingUsers.length > 0 && (
        <p className={cn(mono, 'text-foreground/35 italic')}>
          {t('thread.typing', { names: typingUsers.map((u) => u.name).join(', ') })}
        </p>
      )}
    </div>
  )
}
